import { useState, useEffect, useRef, useCallback } from "react";
import * as api from "../api";
import { reproducirAviso } from "../utils";

const INTERVALO = 60000;                                  // 60 s: un helpdesk no necesita más
const CLAVE_CURSOR = uid => `helpdesk_ultimo_id_${uid || "x"}`;
const CLAVE_HIST   = uid => `helpdesk_avisos_${uid || "x"}`;
const MAX_HIST = 30;

// Avisos de incidencias nuevas.
//
// Consulta por `id` mayor que el último visto, no por fecha: inmune al desfase
// de reloj entre servidores y a la conversión UTC.
//
// Cursor e historial se guardan en localStorage por usuario. Es deliberadamente
// local: si estuviera en el servidor, abrir ALM en el móvil apagaría los avisos
// del portátil.
export function useTicketsNuevos(token, activo, userId) {
  const [avisos, setAvisos] = useState([]);         // tarjetas flotantes, efímeras
  const [historial, setHistorial] = useState([]);   // lo que se ve en la campana
  // Contador que sube con cada tanda de incidencias nuevas. Lo escucha el
  // tablero de Incidencias para recargarse solo: si no, el aviso anuncia un
  // ticket que no esta en la pantalla que tienes delante.
  const [senal, setSenal] = useState(0);
  const cursor  = useRef(null);
  const enVuelo = useRef(false);

  const descartar = useCallback(id => setAvisos(p => p.filter(a => a.id !== id)), []);

  const marcarLeido = useCallback(id => {
    setHistorial(prev => {
      const lista = prev.map(t => (t.id === id ? { ...t, leido: true } : t));
      try { localStorage.setItem(CLAVE_HIST(userId), JSON.stringify(lista)); } catch { /* */ }
      return lista;
    });
  }, [userId]);

  const marcarTodosLeidos = useCallback(() => {
    setHistorial(prev => {
      const lista = prev.map(t => ({ ...t, leido: true }));
      try { localStorage.setItem(CLAVE_HIST(userId), JSON.stringify(lista)); } catch { /* */ }
      return lista;
    });
  }, [userId]);

  const borrar = useCallback(id => {
    setHistorial(prev => {
      const lista = prev.filter(t => t.id !== id);
      try { localStorage.setItem(CLAVE_HIST(userId), JSON.stringify(lista)); } catch { /* */ }
      return lista;
    });
  }, [userId]);

  useEffect(() => {
    if (!token || !activo) return;

    cursor.current = Number(localStorage.getItem(CLAVE_CURSOR(userId))) || null;
    try { setHistorial(JSON.parse(localStorage.getItem(CLAVE_HIST(userId)) || "[]")); }
    catch { setHistorial([]); }

    const consultar = async () => {
      // Con la pestaña oculta no se consulta: el aviso no se vería y solo
      // gastaría llamadas contra Odoo.
      if (document.hidden || enVuelo.current) return;
      enVuelo.current = true;
      try {
        const r = await api.getHelpdeskNuevos(cursor.current);
        if (r.ultimo_id) {
          cursor.current = r.ultimo_id;
          localStorage.setItem(CLAVE_CURSOR(userId), String(r.ultimo_id));
        }
        if (r.tickets?.length) {
          reproducirAviso();
          setSenal(n => n + 1);
          const nuevos = r.tickets.slice().reverse();          // los más recientes arriba
          setAvisos(p => [...nuevos, ...p].slice(0, 6));       // pila flotante acotada
          setHistorial(prev => {
            const vistos = new Set(prev.map(t => t.id));
            const lista = [
              ...nuevos.filter(t => !vistos.has(t.id)).map(t => ({ ...t, leido: false })),
              ...prev,
            ].slice(0, MAX_HIST);
            try { localStorage.setItem(CLAVE_HIST(userId), JSON.stringify(lista)); } catch { /* */ }
            return lista;
          });
        }
      } catch {
        // Un fallo de red o una sesión caducada no deben molestar: el widget de
        // fichaje ya avisa de que hay que reconectar.
      } finally {
        enVuelo.current = false;
      }
    };

    consultar();
    const id = setInterval(consultar, INTERVALO);
    // Al volver a la pestaña se mira enseguida, sin esperar al siguiente ciclo.
    const alVolver = () => { if (!document.hidden) consultar(); };
    document.addEventListener("visibilitychange", alVolver);

    return () => { clearInterval(id); document.removeEventListener("visibilitychange", alVolver); };
  }, [token, activo, userId]);

  const sinLeer = historial.filter(t => !t.leido).length;

  return { avisos, historial, sinLeer, senal, descartar, marcarLeido, marcarTodosLeidos, borrar };
}
